import React from 'react';
import { List, ListItem, ListItemIcon, ListItemText, Typography } from '@mui/material';
import { CheckCircle as CheckCircleIcon, Error as ErrorIcon, RadioButtonUnchecked as PendingIcon, Warning as WarningIcon, } from '@mui/icons-material';
import { useQuery } from 'react-query';
import API from '@wheeebo/polaris-api';
import { isAsv } from './vehicleTerms';
const POLL_INTERVAL_MS = 3000;
const LINK_FRESH_MS = 30000;
const GPS_MIN_SATS = 5;
const GPS_MAX_HDOP = 3.0; // metrics.gps.eph carries HDOP (dimensionless)
// The API serializes GPS fix type as the proto enum name ("FIX_3D",
// "FIX_2D", ...). MAVLink-side mapping folds DGPS/RTK into FIX_3D, but
// accept the richer names defensively as well.
const isGpsFixGood = (fixType) => /3D|DGPS|RTK|PPP|STATIC/i.test(String(fixType ?? ''));
/**
 * Pre-departure checklist — the QGC/MissionPlanner style readiness gate.
 * Polls last metrics while mounted; `onReadyChange` reports whether every
 * required check is green so callers can gate the start button.
 */
function PreflightChecklist({ vehicle, onReadyChange }) {
    const asv = isAsv(vehicle.vehicleType);
    const { data: metrics } = useQuery(API.Vehicles.getLastMetricsKey(vehicle.id), () => API.Vehicles.getLastMetrics(vehicle.id).catch(() => null), 
    // Do not keep previous data: a vehicle switch must not show the
    // previous vehicle's metrics as ready.
    { refetchInterval: POLL_INTERVAL_MS, keepPreviousData: false });
    // vehicles.last_heartbeat_at is updated on every telemetry receipt, so
    // poll the vehicle itself — the prop copy goes stale while mounted.
    const { data: liveVehicle } = useQuery(API.Vehicles.getKey(vehicle.id), () => API.Vehicles.get(vehicle.id).catch(() => null), { refetchInterval: POLL_INTERVAL_MS, keepPreviousData: false });
    const now = Date.now();
    // metrics.time is the 5-minute rollup upsert and is NOT a link clock.
    const heartbeatAt = liveVehicle?.lastHeartbeatAt ?? vehicle.lastHeartbeatAt;
    const linkAge = heartbeatAt ? now - heartbeatAt : undefined;
    const linkOk = linkAge !== undefined && linkAge < LINK_FRESH_MS;
    const gps = metrics?.gps;
    const gpsOk = gps !== undefined &&
        isGpsFixGood(gps.fixType) &&
        (gps.satellitesVisible ?? 0) >= GPS_MIN_SATS &&
        gps.eph > 0 &&
        gps.eph < GPS_MAX_HDOP;
    // Real vehicle telemetry does not populate ekfOk/isArmable (only the
    // demo mocker does) — treat them as advisory when absent.
    const ekfReported = metrics?.ekfOk !== undefined;
    const ekfOk = metrics?.ekfOk === true;
    const batteryLevel = metrics?.battery?.level;
    const items = [
        {
            key: 'link',
            label: 'Telemetry link',
            detail: linkOk ? `${Math.round((linkAge || 0) / 1000)}s ago` : 'stale or no telemetry',
            state: linkAge === undefined ? 'pending' : linkOk ? 'pass' : 'fail',
            required: true,
        },
        {
            key: 'gps',
            label: 'GNSS fix',
            detail: gps
                ? `${String(gps.fixType ?? 'unknown')} · ${gps.satellitesVisible ?? '?'} sats · HDOP ` +
                    `${gps.eph?.toFixed(1)}`
                : 'no GPS data',
            state: metrics === undefined ? 'pending' : gpsOk ? 'pass' : 'fail',
            required: true,
        },
        {
            key: 'ekf',
            label: 'Position estimator (EKF)',
            detail: !ekfReported ? 'not reported by vehicle' : ekfOk ? 'healthy' : 'not healthy',
            state: metrics === undefined ? 'pending' : !ekfReported ? 'pending' : ekfOk ? 'pass' : 'warn',
            required: false,
        },
        {
            key: 'battery',
            label: 'Battery',
            detail: batteryLevel !== undefined ? `${Math.round(batteryLevel * 100)}%` : 'unknown',
            state: batteryLevel === undefined
                ? 'pending'
                : batteryLevel > 0.25
                    ? 'pass'
                    : batteryLevel > 0.1
                        ? 'warn'
                        : 'fail',
            required: false,
        },
        {
            key: 'arm',
            label: asv ? 'Autonomy link' : 'Armable',
            detail: asv
                ? 'CM4 autonomy engages AUTO on mission start'
                : metrics?.armed
                    ? 'armed'
                    : metrics?.isArmable
                        ? 'armable'
                        : 'not armable',
            state: metrics === undefined ? 'pending' : asv ? 'pass' : metrics?.armed || metrics?.isArmable ? 'pass' : 'warn',
            required: false,
        },
    ];
    // vehicleMode arrives as the proto enum name string at runtime.
    const modeName = metrics?.vehicleMode !== undefined ? String(metrics.vehicleMode) : undefined;
    const ready = items.filter((i) => i.required).every((i) => i.state === 'pass');
    React.useEffect(() => {
        onReadyChange?.(ready);
    }, [ready, onReadyChange]);
    return (React.createElement(List, { dense: true, disablePadding: true },
        items.map((item) => (React.createElement(ListItem, { key: item.key, disableGutters: true },
            React.createElement(ListItemIcon, { sx: { minWidth: 34 } }, item.state === 'pass' ? (React.createElement(CheckCircleIcon, { color: "success", fontSize: "small" })) : item.state === 'warn' ? (React.createElement(WarningIcon, { color: "warning", fontSize: "small" })) : item.state === 'pending' ? (React.createElement(PendingIcon, { color: "disabled", fontSize: "small" })) : (React.createElement(ErrorIcon, { color: "error", fontSize: "small" }))),
            React.createElement(ListItemText, { primary: React.createElement(Typography, { variant: "body2" },
                    item.label,
                    item.required ? null : (React.createElement(Typography, { component: "span", variant: "caption", sx: { opacity: 0.6, ml: 0.5 } }, "(advisory)"))), secondary: item.detail })))),
        modeName ? (React.createElement(Typography, { variant: "caption", sx: { display: 'block', mt: 0.5, opacity: 0.7 } },
            "Current mode: ",
            modeName)) : null,
        !ready ? (React.createElement(Typography, { variant: "caption", color: "error", sx: { display: 'block', mt: 0.5 } }, "Required checks are failing \u2014 resolve them before starting the mission.")) : null));
}
export default React.memo(PreflightChecklist);
